import {useEffect, useState} from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Input
} from '@kroll-resolver-crisp/ui-components-react';

function slug(value: string) {
  return value
    .toLowerCase()
    .replaceAll('&', ' and ')
    .replaceAll(/[^\da-z]+/g, '_')
    .replaceAll(/^_|_$/g, '');
}

export function CreateWorkflowDialog({
  open,
  busy,
  existingKeys,
  onClose,
  onCreate
}: {
  readonly open: boolean;
  readonly busy: boolean;
  readonly existingKeys: string[];
  readonly onClose: () => void;
  readonly onCreate: (value: {name: string; key: string}) => void;
}) {
  const [name, setName] = useState('');
  const [key, setKey] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setName('');
      setKey('');
      setTouched(false);
    }
  }, [open]);

  const current = touched ? key : slug(name);
  const error = existingKeys.includes(current)
    ? 'Other guidance already uses this key.'
    : current && !/^[a-z][\d_a-z]*$/.test(current)
      ? 'Lowercase letters, numbers and underscores, starting with a letter.'
      : null;
  const valid = Boolean(name.trim() && current && !error) && !busy;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>New guidance</DialogTitle>
          <DialogDescription>
            Starts as a draft. RD won’t route anything to it until its first configuration is
            approved by someone else.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-4">
            <Field>
              <FieldLabel htmlFor="workflow-new-name">Name</FieldLabel>
              <Input
                autoFocus
                id="workflow-new-name"
                maxLength={256}
                value={name}
                placeholder="e.g. Graphic Violence"
                onChange={(event) => {
                  setName(event.target.value);
                }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="workflow-new-key">Key</FieldLabel>
              <Input
                id="workflow-new-key"
                maxLength={128}
                value={current}
                placeholder="e.g. graphic_violence"
                aria-invalid={Boolean(error)}
                className="font-mono"
                onChange={(event) => {
                  setTouched(true);
                  setKey(event.target.value);
                }}
              />
              <FieldDescription className={error ? 'text-destructive' : undefined}>
                {error ??
                  'Permanent. The RD engine and grounding CSVs refer to this guidance by this key.'}
              </FieldDescription>
            </Field>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!valid}
            onClick={() => {
              onCreate({name: name.trim(), key: current});
            }}>
            Create draft
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
